/**
 * Phase 2-min «row → prefilled calculator» (plan v2 §3 row 2-min, §12 ENTRY
 * resume point; AC-037..045): the PURE mapper from an S13 «Traffic into this
 * funnel» CAMPAIGN row view-model to calculator knob seeds, the URL codec the
 * seeds ride to the Experiments hub, and the overlay that applies them ON TOP
 * of the Phase-1 funnel pull.
 *
 * §6d factorization, amended by the Phase 2 FIRST ITEM (owner directive
 * 2026-09-02, AC-046..054): the row now owns the FULL chain — cps, p1, p2,
 * p3 (SERVED `pressedPeople` ÷ paywall people) — plus the D-side: the
 * accepted-offer mix (takes calibrated per product ÷ pressed), checks per
 * EVERY product and trial→paid. The funnel pull remains the fallback for
 * whatever the row does not serve (older payloads, absent lanes); a seeded
 * row opens with model CAC ≈ the served row CAC, and drift returns only when
 * knobs move.
 *
 * Honesty rules (same as pricingServedPrefill):
 * - every seed carries its counts; the ONLY arithmetic here is a rate from
 *   two SERVED people counts — the S13 view's own sanctioned pattern
 *   (`RateEvidenceCell`, funnelTrafficSourcesRows.ts gate());
 * - a gate that cannot seed produces a TYPED absence with the gate's own
 *   reason — never a bare zero (AC-041);
 * - the row's `breakEvenChecks` is an OUTPUT-shaped figure and NEVER seeds
 *   anything — calculator break-evens recompute from calculator state
 *   (AC-040); only the observed `checksPerSubscriber` seeds the checks knob.
 */

import {
  cloneState,
  solveSolosForTakes,
  type CalculatorModelState,
  type CalculatorOffer,
} from './pricingCalculator';
import type { CalculatorProvenance } from './pricingServedPrefill';
import type { FunnelTrafficRow } from './funnelTrafficSourcesRows';

// ---------------------------------------------------------------------------
// Seed shape — URL-serializable, data only (labels are built at apply time)
// ---------------------------------------------------------------------------

export type RowSeedKnob = 'cps' | 'p1' | 'p2' | 'p3' | 'checks';

export interface RowSeedStat {
  numerator: number;
  denominator: number;
}

/**
 * Phase 2 first item: one product's served facts, keyed by the Stripe price
 * id (the two-id-space rule). Keys are compact on purpose — the list rides
 * the `rowSeed` URL payload.
 */
export interface RowSeedProduct {
  priceId: string;
  /** Accepted subscriptions of the product on this row — the take numerator over pressed. */
  subs: number;
  /** Served checks/sub (Σ paid cycles ÷ subs); absent when no paid cycles yet. */
  checks?: number;
  trialStarted?: number;
  trialConverted?: number;
  /** Cohort age (days from first paid cycle, served) — the maturity caption's fact. */
  ageDays?: number;
  /**
   * Report-mode offer synthesis (owner 2026-09-02, «только для режима
   * отчётов»): the OBSERVED first-paid-cycle price + interval, minor units.
   * Present only when the lane served a single-currency price; lets the
   * apply step BUILD the menu row when the registry has no match. Never
   * present without both the amount and its currency.
   */
  priceMinor?: number;
  currency?: string;
  exponent?: number;
  interval?: string;
  intervalCount?: number;
  trialDays?: number;
}

/** URL-size guard: many-product rows cap to the top sellers (subs desc). */
const ROW_SEED_PRODUCT_CAP = 12;

/** Mirror of the served readiness default `policy.checksFloorSubscriptions` — named in captions. */
export const ROW_CHECKS_THIN_FLOOR = 5;

export interface RowServedSeeds {
  /** The campaign row's display label — named ONCE in the panel alert. */
  campaign: string;
  /** The S13 window the figures were served for (captions + round-trip). */
  window: { from: string; to: string };
  /** `funnel.costPerStart` (shared spend ÷ started) — absent = not seeded. */
  cps?: number;
  /** firstToSecond gate: firstToSecondPeople / firstScreenOpenedPeople. */
  p1?: RowSeedStat;
  /** Of-previous RECOMPUTE: paywallReachedPeople / firstToSecondPeople. */
  p2?: RowSeedStat;
  /** Phase 2 first item: pressedPeople / paywallReachedPeople (both SERVED). */
  p3?: RowSeedStat;
  /**
   * Phase 2 first item: per-product served facts — the accepted mix (takes
   * calibration over pressed), checks per EVERY product, trial→paid, cohort
   * age. Capped to the top sellers; absent on pre-change payloads.
   */
  products?: RowSeedProduct[];
  /** The leading product's served checks/sub + its Stripe price id (v-min legacy — `products` supersedes it when present). */
  checks?: { value: number; productKey?: string };
  /**
   * The row's SERVED CAC (shared spend ÷ new paying people) — a REFERENCE
   * figure only, never a seed: the calculator's CAC is a model output
   * (cps ÷ p1·p2·p3·B) and the two diverge exactly when the campaign's
   * paywall→paid outcome differs from the funnel/model chain. Printing the
   * served figure beside the model one prevents the misread (owner-reported
   * 2026-09-02).
   */
  cac?: { value: number; paying?: number };
  /** Typed absences — the gate's own reason per knob that did NOT seed. */
  absences: Partial<Record<RowSeedKnob, string>>;
}

const formatCount = (value: number): string => value.toLocaleString('en-US');

// ---------------------------------------------------------------------------
// AC-037 / AC-041 / AC-042 — the mapper
// ---------------------------------------------------------------------------

type GateInput = { available: boolean; numerator?: number; denominator?: number; reason?: string };

/** One rate from two served counts, or the typed reason it cannot seed. */
function seedFromCounts(numerator: number | undefined, denominator: number | undefined): { seed?: RowSeedStat; absence?: string } {
  if (typeof numerator !== 'number' || typeof denominator !== 'number' || !Number.isFinite(numerator) || !Number.isFinite(denominator)) {
    return { absence: 'n/a · counts not served' };
  }
  if (denominator <= 0 || numerator === 0) {
    // AC-041/042: a 0 numerator or 0 denominator is an absence with its
    // counts — a bare-zero rate never seeds a knob.
    return { absence: `n/a · ${formatCount(numerator)}/${formatCount(denominator)} observed` };
  }
  if (numerator > denominator) {
    // Defensive BVA: a funnel gate cannot exceed its upstream stage.
    return { absence: 'n/a · inconsistent counts' };
  }
  return { seed: { numerator, denominator } };
}

/**
 * Map one S13 campaign row view-model to calculator seeds. Returns null when
 * the row cannot carry the action at all: creative rows (LOW-trust grain,
 * D-CELL-1 — full Phase 2 territory), the untagged «(not set)» bucket (not a
 * fixable campaign), or a row with nothing seedable.
 */
export function rowServedPrefill(
  row: FunnelTrafficRow,
  window: { from: string; to: string },
): RowServedSeeds | null {
  if (row.kind !== 'campaign' || row.isNoCampaign) return null;
  const seeds: RowServedSeeds = { campaign: row.label, window, absences: {} };

  // cps ← funnel.costPerStart (SERVED; null → typed absence, AC-042).
  if (typeof row.costPerStart.value === 'number' && Number.isFinite(row.costPerStart.value) && row.costPerStart.value > 0) {
    seeds.cps = row.costPerStart.value;
  } else {
    seeds.absences.cps = `n/a · ${row.costPerStart.reason ?? 'not served'}`;
  }

  // p1 ← the row's own firstToSecond gate (rate re-derived from its counts).
  const p1Gate = row.firstToSecond as GateInput;
  if (!p1Gate.available) {
    seeds.absences.p1 = `n/a · ${p1Gate.reason ?? 'gate unavailable'}`;
  } else {
    const p1 = seedFromCounts(p1Gate.numerator, p1Gate.denominator);
    if (p1.seed) seeds.p1 = p1.seed; else seeds.absences.p1 = p1.absence;
  }

  // p2 — of-previous RECOMPUTE (row gates are of-started): paywall people ÷
  // firstToSecond people. Both gates must be available (visits mode marks
  // paywall unavailable with NO_VISIT_TWIN_REASON — that reason surfaces).
  const paywallGate = row.paywall as GateInput;
  if (!paywallGate.available) {
    seeds.absences.p2 = `n/a · ${paywallGate.reason ?? 'gate unavailable'}`;
  } else if (!p1Gate.available) {
    seeds.absences.p2 = `n/a · ${p1Gate.reason ?? 'gate unavailable'}`;
  } else {
    const p2 = seedFromCounts(paywallGate.numerator, p1Gate.numerator);
    if (p2.seed) seeds.p2 = p2.seed; else seeds.absences.p2 = p2.absence;
  }

  // p3 — Phase 2 first item (AC-049): SERVED pressedPeople ÷ the paywall
  // gate's own numerator. Absent pressed (pre-change payload) and an
  // unavailable paywall gate are DIFFERENT absences with their own reasons.
  if (!paywallGate.available) {
    seeds.absences.p3 = `n/a · ${paywallGate.reason ?? 'gate unavailable'}`;
  } else if (typeof row.pressedPeople !== 'number') {
    seeds.absences.p3 = 'n/a · pressed count not served (older payload)';
  } else {
    const p3 = seedFromCounts(row.pressedPeople, paywallGate.numerator);
    if (p3.seed) seeds.p3 = p3.seed; else seeds.absences.p3 = p3.absence;
  }

  // Products — Phase 2 first item (AC-050): verbatim served facts, capped to
  // the top sellers by subscriptions so the URL payload stays bounded.
  if (row.productFacts?.length) {
    const products = [...row.productFacts]
      .filter(product => product.subscriptions > 0)
      .sort((left, right) => right.subscriptions - left.subscriptions)
      .slice(0, ROW_SEED_PRODUCT_CAP)
      .map((product): RowSeedProduct => ({
        priceId: product.priceId,
        subs: product.subscriptions,
        ...(typeof product.checksPerSubscriber === 'number' && product.checksPerSubscriber > 0
          ? { checks: product.checksPerSubscriber }
          : {}),
        ...(typeof product.trialStarted === 'number' && product.trialStarted > 0
          ? { trialStarted: product.trialStarted, trialConverted: product.trialConverted ?? 0 }
          : {}),
        ...(typeof product.ageDaysAvg === 'number' ? { ageDays: Math.round(product.ageDaysAvg) } : {}),
        // Observed money facts (synthesis): amount AND currency or neither
        // (mixed currency serves priceCurrency null — SI-5 typed absence).
        ...(typeof product.priceMinor === 'number' && product.priceMinor > 0 && typeof product.priceCurrency === 'string'
          ? {
            priceMinor: product.priceMinor,
            currency: product.priceCurrency,
            ...(typeof product.priceExponent === 'number' ? { exponent: product.priceExponent } : {}),
            ...(typeof product.planInterval === 'string' ? { interval: product.planInterval } : {}),
            ...(typeof product.planIntervalCount === 'number' ? { intervalCount: product.planIntervalCount } : {}),
            ...(typeof product.trialDaysMax === 'number' && product.trialDaysMax > 0 ? { trialDays: product.trialDaysMax } : {}),
          }
          : {}),
      }));
    if (products.length > 0) seeds.products = products;
  }

  // checks ← the leading product's observed checks/sub (share block C1).
  // `breakEvenChecks` is NOT read here — AC-040.
  if (typeof row.checksPerSubscriber === 'number' && Number.isFinite(row.checksPerSubscriber) && row.checksPerSubscriber > 0) {
    seeds.checks = { value: row.checksPerSubscriber, ...(row.checksProductKey ? { productKey: row.checksProductKey } : {}) };
  } else {
    seeds.absences.checks = 'n/a · no served checks on this row (no paid cycles or an older payload)';
  }

  // Served CAC reference (not a knob — applyRowSeeds never touches it).
  if (typeof row.cac.value === 'number' && Number.isFinite(row.cac.value) && row.cac.value > 0) {
    seeds.cac = { value: row.cac.value, ...(typeof row.cac.count === 'number' ? { paying: row.cac.count } : {}) };
  }

  const hasSeed = seeds.cps !== undefined || seeds.p1 !== undefined || seeds.p2 !== undefined
    || seeds.p3 !== undefined || seeds.checks !== undefined || seeds.products !== undefined;
  return hasSeed ? seeds : null;
}

// ---------------------------------------------------------------------------
// URL codec — the seeds ride `?tab=pricing&…&rowSeed=<json>` to the hub
// ---------------------------------------------------------------------------

export const ROW_PREFILL_PARAMS = {
  campaign: 'campaign',
  from: 'from',
  to: 'to',
  onboardingId: 'onboardingId',
  versionId: 'versionId',
  seed: 'rowSeed',
} as const;

export interface RowPrefillRequest {
  seeds: RowServedSeeds;
  /** The S13 page's funnel scope — the Phase-1 pull runs against it (AC-038). */
  scope?: { onboardingId: string; versionId: string; from: string; to: string };
}

export function buildRowEconomicsPath(target: {
  projectId: string;
  onboardingId: string;
  versionId: string;
  from: string;
  to: string;
  /** The row's stable key (meta campaign id) — identification + round-trip. */
  campaignKey: string;
  seeds: RowServedSeeds;
}): string {
  const params = new URLSearchParams({
    tab: 'pricing',
    [ROW_PREFILL_PARAMS.campaign]: target.campaignKey,
    [ROW_PREFILL_PARAMS.from]: target.from,
    [ROW_PREFILL_PARAMS.to]: target.to,
    [ROW_PREFILL_PARAMS.onboardingId]: target.onboardingId,
    [ROW_PREFILL_PARAMS.versionId]: target.versionId,
    [ROW_PREFILL_PARAMS.seed]: JSON.stringify(target.seeds),
  });
  return `/project/${encodeURIComponent(target.projectId)}/experiments?${params.toString()}`;
}

const isStat = (value: unknown): value is RowSeedStat => (
  typeof value === 'object' && value !== null
  && typeof (value as RowSeedStat).numerator === 'number'
  && typeof (value as RowSeedStat).denominator === 'number'
  && (value as RowSeedStat).numerator > 0
  && (value as RowSeedStat).denominator >= (value as RowSeedStat).numerator
);

/**
 * Parse the seeds back from the hub URL. A malformed or partially invalid
 * payload yields null — the calculator then behaves as if no row prefill was
 * requested (never a crash, never a silently wrong seed).
 */
export function parseRowPrefillParams(params: URLSearchParams): RowPrefillRequest | null {
  const raw = params.get(ROW_PREFILL_PARAMS.seed);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const candidate = parsed as Record<string, unknown>;
  const window = candidate.window as Record<string, unknown> | undefined;
  if (typeof candidate.campaign !== 'string' || candidate.campaign.length === 0) return null;
  if (!window || typeof window.from !== 'string' || typeof window.to !== 'string') return null;
  const seeds: RowServedSeeds = {
    campaign: candidate.campaign,
    window: { from: window.from, to: window.to },
    absences: {},
  };
  if (typeof candidate.cps === 'number' && Number.isFinite(candidate.cps) && candidate.cps > 0) seeds.cps = candidate.cps;
  if (isStat(candidate.p1)) seeds.p1 = candidate.p1 as RowSeedStat;
  if (isStat(candidate.p2)) seeds.p2 = candidate.p2 as RowSeedStat;
  if (isStat(candidate.p3)) seeds.p3 = candidate.p3 as RowSeedStat;
  // Products validate PER ITEM (AC-050): a malformed entry drops, the rest stand.
  if (Array.isArray(candidate.products)) {
    const products = (candidate.products as unknown[])
      .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
      .filter(entry => typeof entry.priceId === 'string' && entry.priceId.length > 0
        && typeof entry.subs === 'number' && Number.isFinite(entry.subs) && entry.subs > 0)
      .slice(0, ROW_SEED_PRODUCT_CAP)
      .map((entry): RowSeedProduct => ({
        priceId: entry.priceId as string,
        subs: entry.subs as number,
        ...(typeof entry.checks === 'number' && Number.isFinite(entry.checks) && entry.checks > 0 ? { checks: entry.checks } : {}),
        ...(typeof entry.trialStarted === 'number' && entry.trialStarted > 0
          ? { trialStarted: entry.trialStarted, trialConverted: typeof entry.trialConverted === 'number' && entry.trialConverted >= 0 ? entry.trialConverted : 0 }
          : {}),
        ...(typeof entry.ageDays === 'number' && Number.isFinite(entry.ageDays) ? { ageDays: entry.ageDays } : {}),
        // Money facts validate as a PAIR — an amount without its currency drops.
        ...(typeof entry.priceMinor === 'number' && Number.isFinite(entry.priceMinor) && entry.priceMinor > 0 && typeof entry.currency === 'string' && entry.currency.length > 0
          ? {
            priceMinor: entry.priceMinor,
            currency: entry.currency,
            ...(typeof entry.exponent === 'number' && Number.isFinite(entry.exponent) ? { exponent: entry.exponent } : {}),
            ...(typeof entry.interval === 'string' && entry.interval.length > 0 ? { interval: entry.interval } : {}),
            ...(typeof entry.intervalCount === 'number' && entry.intervalCount > 0 ? { intervalCount: entry.intervalCount } : {}),
            ...(typeof entry.trialDays === 'number' && entry.trialDays > 0 ? { trialDays: entry.trialDays } : {}),
          }
          : {}),
      }));
    if (products.length > 0) seeds.products = products;
  }
  const checks = candidate.checks as Record<string, unknown> | undefined;
  if (checks && typeof checks.value === 'number' && Number.isFinite(checks.value) && checks.value > 0) {
    seeds.checks = { value: checks.value, ...(typeof checks.productKey === 'string' ? { productKey: checks.productKey } : {}) };
  }
  const cac = candidate.cac as Record<string, unknown> | undefined;
  if (cac && typeof cac.value === 'number' && Number.isFinite(cac.value) && cac.value > 0) {
    seeds.cac = { value: cac.value, ...(typeof cac.paying === 'number' ? { paying: cac.paying } : {}) };
  }
  const absences = candidate.absences as Record<string, unknown> | undefined;
  if (absences && typeof absences === 'object') {
    (['cps', 'p1', 'p2', 'p3', 'checks'] as const).forEach(knob => {
      if (typeof absences[knob] === 'string') seeds.absences[knob] = absences[knob] as string;
    });
  }
  if (seeds.cps === undefined && !seeds.p1 && !seeds.p2 && !seeds.p3 && !seeds.checks && !seeds.products) return null;

  const onboardingId = params.get(ROW_PREFILL_PARAMS.onboardingId);
  const versionId = params.get(ROW_PREFILL_PARAMS.versionId);
  const from = params.get(ROW_PREFILL_PARAMS.from);
  const to = params.get(ROW_PREFILL_PARAMS.to);
  const scope = onboardingId && versionId && from && to
    ? { onboardingId, versionId, from, to }
    : undefined;
  return { seeds, ...(scope ? { scope } : {}) };
}

// ---------------------------------------------------------------------------
// AC-039 / AC-040 — apply row seeds ON TOP of the current calculator state
// ---------------------------------------------------------------------------

export const KNOB_HUMAN_LABELS: Record<RowSeedKnob, string> = {
  cps: 'cost per start',
  p1: 'first → second',
  p2: 'second → paywall',
  p3: 'paywall → pressed',
  checks: 'checks per subscriber',
};

export interface RowSeedApplyResult {
  state: CalculatorModelState;
  provenance: CalculatorProvenance;
  /** Honesty captions for the panel alert (absences, unmatched checks). */
  notes: string[];
  /**
   * Report-mode synthesis (owner 2026-09-02): offers BUILT from the row's
   * observed products when the registry carries no match — the caller merges
   * them into the palette. MODELING-ONLY entities: registering an experiment
   * on one still requires a configured paywall product (3a boundary). Empty
   * when every row product matched (the normal, registry-backed case).
   */
  offers: Record<string, CalculatorOffer>;
}

const FAMILY_BY_ROW_INTERVAL: Record<string, CalculatorOffer['family']> = {
  day: 'daily',
  week: 'weekly',
  month: 'monthly',
  year: 'annual',
};

const INTERVAL_WORD: Record<string, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  year: 'Annual',
};

/**
 * Build a MODELING offer from the row's observed money facts (report mode
 * only — the caller reaches here exclusively via a rowSeed open). The price
 * is the served first-paid-cycle amount — observed money, not a scenario.
 */
function offerFromRowProduct(product: RowSeedProduct): CalculatorOffer | null {
  if (typeof product.priceMinor !== 'number' || !product.currency) return null;
  const exponent = typeof product.exponent === 'number' ? product.exponent : 2;
  const price = product.priceMinor / 10 ** exponent;
  if (!Number.isFinite(price) || price <= 0) return null;
  const interval = product.interval ?? '';
  const family = FAMILY_BY_ROW_INTERVAL[interval] ?? 'monthly';
  const count = product.intervalCount ?? 1;
  const cadence = count > 1 ? `Every ${count} ${interval}s` : (INTERVAL_WORD[interval] ?? 'Subscription');
  const currencyTag = product.currency.toUpperCase() === 'USD' ? '' : ` ${product.currency.toUpperCase()}`;
  return {
    id: `row_${product.priceId}`,
    label: `${cadence} $${price.toFixed(2)}${currencyTag} · row`,
    family,
    price,
    priceState: 'observed',
    ...(typeof product.trialDays === 'number' && product.trialDays > 0 ? { trialDays: product.trialDays } : {}),
    isSubscription: true,
    stripePriceId: product.priceId,
  };
}

export const rateLabel = (stat: RowSeedStat): string => {
  const rate = stat.numerator / stat.denominator;
  return `row: observed ${(rate * 100).toFixed(1)}% · ${formatCount(stat.numerator)}/${formatCount(stat.denominator)}`;
};

/**
 * Overlay the row seeds onto a COPY of the calculator state. Runs AFTER the
 * Phase-1 funnel pull (or alone when the pull fails/never runs — AC-043):
 * present seeds OVERWRITE the funnel value on their knob (§6d: the row owns
 * the left half as direct levers) with provenance kind `row`; typed absences
 * never touch a knob — they become notes, and any funnel-pulled value on
 * that knob stands.
 */
export function applyRowSeeds(input: {
  base: CalculatorModelState;
  provenance: CalculatorProvenance | null;
  offers: Record<string, CalculatorOffer>;
  seeds: RowServedSeeds;
}): RowSeedApplyResult {
  const { seeds, offers } = input;
  const state = cloneState(input.base);
  const provenance: CalculatorProvenance = input.provenance
    ? (JSON.parse(JSON.stringify(input.provenance)) as CalculatorProvenance)
    : { chain: {}, anchors: {}, checks: {}, trialConv: {}, refundShare: {} };
  const notes: string[] = [];

  if (seeds.cps !== undefined) {
    state.chain.cps = seeds.cps;
    provenance.chain.cps = { kind: 'row', label: `row: observed $${seeds.cps.toFixed(2)} per start` };
  }
  if (seeds.p1) {
    state.chain.p1 = seeds.p1.numerator / seeds.p1.denominator;
    provenance.chain.p1 = { kind: 'row', label: rateLabel(seeds.p1) };
  }
  if (seeds.p2) {
    state.chain.p2 = seeds.p2.numerator / seeds.p2.denominator;
    provenance.chain.p2 = { kind: 'row', label: rateLabel(seeds.p2) };
  }
  if (seeds.p3) {
    // Phase 2 first item: the row now owns p3 too (SERVED pressedPeople) —
    // it overwrites the funnel ladder's p3 exactly like the other knobs.
    state.chain.p3 = seeds.p3.numerator / seeds.p3.denominator;
    provenance.chain.p3 = { kind: 'row', label: rateLabel(seeds.p3) };
  }

  // Phase 2 first item — the D-side (AC-051/052): calibrate the menu takes to
  // the row's accepted-offer mix and seed checks + trial→paid per product.
  const synthesized: Record<string, CalculatorOffer> = {};
  if (seeds.products?.length) {
    const pressed = seeds.p3?.numerator;
    const matched: Array<{ offer: CalculatorOffer; product: RowSeedProduct }> = [];
    const unmatched: RowSeedProduct[] = [];
    seeds.products.forEach(product => {
      const offer = Object.values(offers).find(candidate => candidate.stripePriceId === product.priceId);
      if (offer && offer.price !== null) {
        matched.push({ offer, product });
        return;
      }
      // Report-mode synthesis (owner 2026-09-02): no registry match, but the
      // row OBSERVED this product's price — build the menu row from the
      // served facts instead of dropping the mix on the floor.
      const built = offerFromRowProduct(product);
      if (built) {
        synthesized[built.id] = built;
        matched.push({ offer: built, product });
      } else {
        unmatched.push(product);
      }
    });
    if (Object.keys(synthesized).length > 0) {
      notes.push(
        `${Object.keys(synthesized).length} offer(s) built from the campaign row's observed products (not in the project registry — modeling only, register the product to experiment on it): ${Object.values(synthesized).map(offer => offer.label).join(', ')}.`,
      );
    }
    if (unmatched.length > 0) {
      notes.push(
        `${unmatched.length} row product(s) match no palette offer and served no usable price — their mix and checks stay out (price ids: ${unmatched.map(product => product.priceId).join(', ')}).`,
      );
    }

    // Takes calibration (AC-051): targetTakes = accepted subs ÷ pressed —
    // the row-grain twin of the Phase-1 paid|pressed anchors; solos come from
    // the SAME solver, never by writing takes directly.
    if (matched.length > 0 && typeof pressed === 'number' && pressed > 0) {
      const offerPool = { ...offers, ...synthesized };
      const observedMenu = matched.map(entry => entry.offer.id);
      const targetTakes: Record<string, number> = {};
      matched.forEach(({ offer, product }) => {
        targetTakes[offer.id] = product.subs / pressed;
      });
      // AC-056 (owner checkpoint finding): payers WITHOUT product attribution
      // (unknown-product residual / one-time buys) are real people the served
      // CAC divides by — they join B as an unpriced residual take (both
      // counts SERVED), or the model reads high by exactly their share.
      const matchedSubs = matched.reduce((sum, entry) => sum + entry.product.subs, 0);
      const paying = seeds.cac?.paying;
      if (typeof paying === 'number' && paying > matchedSubs) {
        const residual = paying - matchedSubs;
        const residualOffer: CalculatorOffer = {
          id: 'row_residual',
          label: 'Other paid conversions · row',
          family: 'one_time',
          price: 0,
          priceState: 'scenario',
          isSubscription: false,
        };
        synthesized[residualOffer.id] = residualOffer;
        offerPool[residualOffer.id] = residualOffer;
        observedMenu.push(residualOffer.id);
        targetTakes[residualOffer.id] = residual / pressed;
        provenance.anchors[residualOffer.id] = {
          kind: 'row',
          label: `row: observed take ${((residual / pressed) * 100).toFixed(1)}% · ${formatCount(residual)}/${formatCount(pressed)} pressed — product unknown, revenue not modeled`,
        };
        notes.push(
          `${formatCount(residual)} of ${formatCount(paying)} paying people carry no product attribution — they count toward CAC as buyers with unmodeled ($0) revenue.`,
        );
      }
      const solved = solveSolosForTakes(observedMenu, offerPool, targetTakes);
      if (!solved && synthesized.row_residual) {
        // A refused calibration must not leave the residual pseudo-offer (or
        // its claims) behind — drop it and its provenance/note.
        delete synthesized.row_residual;
        delete provenance.anchors.row_residual;
        const residualNoteAt = notes.findIndex(note => note.includes('no product attribution'));
        if (residualNoteAt >= 0) notes.splice(residualNoteAt, 1);
      }
      if (solved) {
        const removed = state.menu.filter(id => !observedMenu.includes(id));
        if (removed.length > 0) {
          notes.push(
            `Menu set to the ${observedMenu.length} offer(s) the campaign row actually sold; removed from the menu (still addable): ${removed.map(id => offers[id]?.label ?? id).join(', ')}.`,
          );
        }
        state.menu = observedMenu;
        Object.entries(solved).forEach(([id, solo]) => {
          state.solos[id] = solo;
        });
        matched.forEach(({ offer, product }) => {
          provenance.anchors[offer.id] = {
            kind: 'row',
            label: `row: calibrated to observed take ${((product.subs / pressed) * 100).toFixed(1)}% · ${formatCount(product.subs)}/${formatCount(pressed)} pressed`,
          };
        });
      } else {
        notes.push('Row mix is not representable by the choice model (Σ takes too high) — anchors keep their values.');
      }
    } else if (matched.length > 0) {
      notes.push('Row products served without a pressed count — the mix cannot calibrate takes (anchors keep their values).');
    }

    // Checks + trial→paid per product (AC-052) — captions carry the counts,
    // the cohort age and the named thin floor; a 0-conversion trial cohort is
    // a note, never a 0% seed (it may simply be immature).
    matched.forEach(({ offer, product }) => {
      if (typeof product.checks === 'number' && product.checks > 0) {
        state.checks[offer.id] = product.checks;
        const thin = product.subs < ROW_CHECKS_THIN_FLOOR ? ` · thin (below the ${ROW_CHECKS_THIN_FLOOR}-sub floor)` : '';
        const age = typeof product.ageDays === 'number' ? ` · cohort ~${formatCount(product.ageDays)} d` : '';
        provenance.checks[offer.id] = {
          kind: 'row',
          label: `row: observed ${product.checks.toFixed(2)} checks / sub · ${formatCount(product.subs)} subs${age}${thin}`,
        };
      }
      if ((offer.trialDays ?? 0) > 0 && typeof product.trialStarted === 'number' && product.trialStarted > 0) {
        const converted = product.trialConverted ?? 0;
        if (converted > 0) {
          state.trialConv[offer.id] = converted / product.trialStarted;
          provenance.trialConv[offer.id] = {
            kind: 'row',
            label: `row: observed ${((converted / product.trialStarted) * 100).toFixed(1)}% · ${formatCount(converted)}/${formatCount(product.trialStarted)} trials`,
          };
        } else {
          notes.push(
            `Row trial→paid for ${offer.label}: 0/${formatCount(product.trialStarted)} so far — the knob keeps its value (the cohort may be immature).`,
          );
        }
      }
    });

  }

  if (seeds.checks && !seeds.products?.length) {
    // Join on the provider price id (the two-id-space rule) — the row's
    // checks belong to its LEADING product, never to the whole menu.
    const offer = seeds.checks.productKey
      ? Object.values(offers).find(candidate => candidate.stripePriceId === seeds.checks!.productKey)
      : undefined;
    if (offer) {
      state.checks[offer.id] = seeds.checks.value;
      provenance.checks[offer.id] = {
        kind: 'row',
        label: `row: observed ${seeds.checks.value.toFixed(2)} checks / sub`,
      };
    } else {
      // Review P1-4: lead with the readable fact; the raw provider id is a
      // parenthetical technical detail, not the sentence's subject.
      notes.push(
        `Row checks ${seeds.checks.value.toFixed(2)} / sub come from a product this palette does not carry — the checks knobs keep their values (price id ${seeds.checks.productKey ?? 'unknown'}).`,
      );
    }
  }

  // AC-052 basis honesty: any row-seeded checks put the reading on the
  // 12-month model — the seed enables its mode (AC-007 gesture rule).
  if (Object.values(provenance.checks).some(entry => entry.kind === 'row')) {
    state.basis = 'ltv12';
  }

  (Object.entries(seeds.absences) as Array<[RowSeedKnob, string]>).forEach(([knob, reason]) => {
    notes.push(`Row ${KNOB_HUMAN_LABELS[knob]}: ${reason} — the knob keeps its current value.`);
  });

  return { state, provenance, notes, offers: synthesized };
}
